/**
 * CLOUDS — mockup-style clouds (day.html / night.html): a flat-bottomed rounded
 * "pill" body with two round bumps on top, drifting continuously, with the body
 * colour/alpha blending between the day and night looks.
 */

import {
  clamp01,
  lerpRgb,
  rgb,
  timeSec,
  nightAmount,
  mockupUiScaleFromOpts,
} from "./sky-utils.js";
import { combineSkyDecorSeed, seededHash01 } from "../../core/day-night.js";

export var CLOUD_COUNT = 7;
var DAY_CLOUD_RGB = [255, 255, 255];
var NIGHT_CLOUD_RGB = [106, 127, 173];
/** Contact-shadow blur at scaleUi=1 (day). GPU sky must use the same formula. */
export var CLOUD_SHADOW_BLUR_AT_1 = 3;
/** Extra blur pad at scaleUi=1 so the contact shadow does not clip. */
export var CLOUD_SHADOW_BLUR_PAD_AT_1 = 0.5;
/** Contact-shadow Y offset at scaleUi=1 (day). Keep ≥ blur so it sits under, not as a rim. */
export var CLOUD_SHADOW_OFFSET_Y_AT_1 = 4;

/**
 * Canvas / GPU contact-shadow sizes. Must scale with layout — a small YouTube
 * preview window makes unscaled shadows look huge next to the cloud body.
 * Offset stays ≥ blur so the shadow sits under the cloud, not as a rim halo.
 * @param {number} scaleUi
 * @param {number} [nightAmount]
 * @returns {{ blur: number, offsetY: number }}
 */
export function cloudDropShadow(scaleUi, nightAmount) {
  var s = Math.max(0.01, Number(scaleUi) || 1);
  var day = 1 - Math.max(0, Math.min(1, Number(nightAmount) || 0));
  return {
    blur: Math.max(0.5, CLOUD_SHADOW_BLUR_AT_1 * s * day + CLOUD_SHADOW_BLUR_PAD_AT_1 * s),
    offsetY: CLOUD_SHADOW_OFFSET_Y_AT_1 * s * day,
  };
}

/**
 * Pixel layout for one seeded cloud at a UI scale (includes contact-shadow pad
 * so a transparent PNG export can crop tightly without clipping).
 * @param {number} cloudSeed
 * @param {number} scaleUi
 * @returns {{
 *   unit: number,
 *   shape: { bodyW: number, bodyH: number, bumps: object[], minTop: number },
 *   cloudScale: number,
 *   bodyW: number,
 *   bodyH: number,
 *   width: number,
 *   height: number,
 *   bodyOffsetX: number,
 *   bodyOffsetY: number,
 * }}
 */
export function measureCloudLayout(cloudSeed, scaleUi) {
  var s = Math.max(0.01, Number(scaleUi) || 1);
  var h5 = seededHash01(cloudSeed, 7.19);
  var cloudScale = 0.7 + h5 * 0.5;
  var unit = s * cloudScale;
  var shape = makeCloudShape(cloudSeed);
  var bodyW = shape.bodyW * unit;
  var bodyH = shape.bodyH * unit;
  var topExtent = -shape.minTop * unit;
  var drop = cloudDropShadow(s, 0);
  var shadowBlur = drop.blur;
  var shadowOffsetY = drop.offsetY;
  var pad = Math.ceil(shadowBlur + 4);
  var bodyOffsetX = pad;
  var bodyOffsetY = pad + Math.ceil(topExtent);
  return {
    unit: unit,
    shape: shape,
    cloudScale: cloudScale,
    bodyW: bodyW,
    bodyH: bodyH,
    width: Math.ceil(bodyW + pad * 2),
    height: Math.ceil(bodyOffsetY + bodyH + shadowOffsetY + pad),
    bodyOffsetX: bodyOffsetX,
    bodyOffsetY: bodyOffsetY,
  };
}

/**
 * Draw one cloud with body top-left at `(ox, oy)`. Shape/size come from `cloudSeed`.
 * Time-of-day look is controlled via the same opts as `drawClouds`
 * (`timeOfDay`, `cloudDayBoundary`, `cloudWarmth`, `cloudWarmColors`, `cloudLight`).
 * Pass `scaleUi` (or `layout` + `bounds`) to size the cloud.
 *
 * @param {CanvasRenderingContext2D} ctx
 * @param {number} ox body left edge
 * @param {number} oy body top edge
 * @param {number} cloudSeed
 * @param {{
 *   scaleUi?: number,
 *   layout?: object,
 *   bounds?: { width: number, height?: number },
 *   timeOfDay?: number,
 *   cloudDayBoundary?: number,
 *   cloudWarmth?: number,
 *   cloudWarmColors?: { top: number[], mid: number[], bot: number[] },
 *   cloudLight?: { x: number, y: number, glowRadius: number, color: number[], strength: number }|null,
 * }} [opts]
 */
export function drawSingleCloud(ctx, ox, oy, cloudSeed, opts) {
  if (!ctx) return;
  opts = opts || {};
  var scaleUi = opts.scaleUi;
  if (!(scaleUi > 0)) {
    scaleUi = mockupUiScaleFromOpts(opts, opts.bounds || { width: 720, height: 400 });
  }
  var night = nightAmount(opts.timeOfDay != null ? opts.timeOfDay : 0.25);
  var boundary = opts.cloudDayBoundary;
  var legacyCol = boundary == null ? lerpRgb(DAY_CLOUD_RGB, NIGHT_CLOUD_RGB, night) : null;

  var h5 = seededHash01(cloudSeed, 7.19);
  var h6 = seededHash01(cloudSeed, 6.37);
  var cloudScale = 0.7 + h5 * 0.5;
  var unit = scaleUi * cloudScale;
  var shape = makeCloudShape(cloudSeed);
  var cloudW = shape.bodyW * unit;

  var alpha = 0.82 + h6 * 0.13;
  alpha += (1 - alpha) * night;
  if (alpha <= 0.01) return;

  var yTop = oy + shape.minTop * unit;
  var yBot = oy + shape.bodyH * unit;

  ctx.save();
  ctx.fillStyle = cloudFill(ctx, yTop, yBot, boundary, legacyCol, alpha);
  var warmth = opts.cloudWarmth || 0;
  var warmTarget = opts.cloudWarmColors ? opts.cloudWarmColors.bot : [236, 152, 120];
  var shadowRgb = lerpRgb([120, 170, 200], warmTarget, warmth);
  var drop = cloudDropShadow(scaleUi, night);
  ctx.shadowColor = rgb(shadowRgb, 0.16 * (1 - night));
  ctx.shadowBlur = drop.blur;
  ctx.shadowOffsetY = drop.offsetY;
  drawCloudShape(ctx, ox, oy, unit, shape);
  ctx.restore();

  applyCloudFluff(ctx, ox, oy, unit, shape, yTop, yBot, night);

  if (opts.cloudWarmth > 0) {
    applyCloudWarmth(ctx, ox, oy, unit, shape, yTop, yBot, opts.cloudWarmth, opts.cloudWarmColors);
  }

  if (opts.cloudLight) {
    applyCloudLight(ctx, ox, oy, unit, shape, cloudW, yTop, yBot, opts.cloudLight);
  }
}

/**
 * @param {CanvasRenderingContext2D} ctx
 * @param {{ x: number, y: number, width: number, height: number }} bounds
 * @param {number} currentTime
 * @param {number} decorSeed
 * @param {{ layout?: object, timeOfDay?: number }} [opts]
 */
/**
 * Deterministic cloud placement for one frame (2D draw and the GPU sky shader).
 * @returns {{ seed: number, ox: number, oy: number, unit: number, bodyW: number, bodyH: number, bumpA: {x:number,y:number,r:number}, bumpB: {x:number,y:number,r:number} }[]}
 */
export function layoutSkyClouds(bounds, currentTime, decorSeed, opts) {
  opts = opts || {};
  if (!bounds) return [];
  var x = bounds.x;
  var y = bounds.y;
  var w = Math.max(0, bounds.width);
  var h = Math.max(0, bounds.height);
  if (w <= 0 || h <= 0) return [];

  var scaleUi = mockupUiScaleFromOpts(opts, bounds);
  var t = timeSec(currentTime);
  var bandTop = y + h * 0.04;
  var bandBottom = opts.cloudBandBottomY != null
    ? opts.cloudBandBottomY
    : bandTop + h * 0.14;
  var bandH = Math.max(0, bandBottom - bandTop);
  var out = [];

  for (var i = 0; i < CLOUD_COUNT; i++) {
    var cloudSeed = decorSeed != null
      ? combineSkyDecorSeed(decorSeed, i + 1)
      : i + 1;
    var h1 = seededHash01(cloudSeed, 5.11);
    var h2 = seededHash01(cloudSeed, 9.43);
    var h4 = seededHash01(cloudSeed, 4.91);
    var h5 = seededHash01(cloudSeed, 7.19);
    var shape = makeCloudShape(cloudSeed);
    var cloudScale = 0.7 + h5 * 0.5;
    var unit = scaleUi * cloudScale;
    var cloudW = shape.bodyW * unit;
    var spanW = w + cloudW * 2 + 160 * scaleUi;
    var drift = (t * (6 + h1 * 8) + h2 * spanW) % spanW;
    var ox = x - cloudW + drift;
    var oy = bandTop + h4 * bandH;
    var bumps = shape.bumps || [];
    out.push({
      seed: cloudSeed,
      ox: ox,
      oy: oy,
      unit: unit,
      bodyW: cloudW,
      bodyH: shape.bodyH * unit,
      bumpA: cloudBumpCenter(ox, oy, unit, bumps[0]),
      bumpB: cloudBumpCenter(ox, oy, unit, bumps[1]),
    });
  }
  return out;
}

function cloudBumpCenter(ox, oy, unit, bump) {
  if (!bump) return { x: 0, y: 0, r: 0 };
  return {
    x: ox + (bump.l + bump.d * 0.5) * unit,
    y: oy + (bump.top + bump.d * 0.5) * unit,
    r: bump.d * 0.5 * unit,
  };
}

export function drawClouds(ctx, bounds, currentTime, decorSeed, opts) {
  if (!ctx || !bounds) return;
  opts = opts || {};
  var placed = layoutSkyClouds(bounds, currentTime, decorSeed, opts);
  if (!placed.length) return;
  var scaleUi = mockupUiScaleFromOpts(opts, bounds);
  for (var i = 0; i < placed.length; i++) {
    drawSingleCloud(ctx, placed[i].ox, placed[i].oy, placed[i].seed, {
      scaleUi: scaleUi,
      timeOfDay: opts.timeOfDay,
      cloudDayBoundary: opts.cloudDayBoundary,
      cloudWarmth: opts.cloudWarmth,
      cloudWarmColors: opts.cloudWarmColors,
      cloudLight: opts.cloudLight,
    });
  }
}

/**
 * Golden-hour tint for one cloud: clip to the cloud and lay a warm vertical
 * gradient over it — a light gold top fading to a deeper peach underside — at a
 * strength set by `warmth` (0 = none, 1 = peak sunrise/sunset). Unlike the
 * directional light this is an alpha tint (not additive), so it can pull even a
 * white day cloud toward peach the way low golden light actually does.
 */
function applyCloudWarmth(ctx, ox, oy, unit, shape, yTop, yBot, warmth, colors) {
  if (warmth <= 0.002) return;
  var c = colors || { top: [255, 236, 206], mid: [255, 198, 150], bot: [248, 152, 118] };
  ctx.save();
  var g = ctx.createLinearGradient(0, yTop, 0, yBot);
  g.addColorStop(0, rgb(c.top, warmth * 0.22));
  g.addColorStop(0.55, rgb(c.mid, warmth * 0.42));
  g.addColorStop(1, rgb(c.bot, warmth * 0.58));
  ctx.fillStyle = g;
  // Re-fill the exact cloud path (not a clipped rect) so the tint covers the
  // same anti-aliased edge as the body fill — no untinted white rim.
  drawCloudShape(ctx, ox, oy, unit, shape);
  ctx.restore();
}

/**
 * Brighten one cloud where the sun/moon shines on it: clip to the cloud, then
 * additively paint a radial glow centred on the body so the facing edge lights up
 * and the far edge stays neutral. Intensity also falls off with the cloud's
 * distance from the body, so only nearby clouds are lit.
 */
function applyCloudLight(ctx, ox, oy, unit, shape, cloudW, yTop, yBot, light) {
  var glowR = light.glowRadius;
  if (glowR <= 0 || light.strength <= 0) return;
  var ccx = ox + cloudW * 0.5;
  var ccy = (yTop + yBot) * 0.5;
  var dx = ccx - light.x;
  var dy = ccy - light.y;
  var dist = Math.sqrt(dx * dx + dy * dy);
  var falloff = 1 - clamp01(dist / glowR);
  if (falloff <= 0.002) return;

  var a = light.strength * falloff;
  ctx.save();
  ctx.globalCompositeOperation = "lighter";
  var g = ctx.createRadialGradient(light.x, light.y, 0, light.x, light.y, glowR);
  g.addColorStop(0, rgb(light.color, a));
  g.addColorStop(0.55, rgb(light.color, a * 0.45));
  g.addColorStop(1, rgb(light.color, 0));
  ctx.fillStyle = g;
  // Light the body only — a blurred fringe would paint a coloured outline on the sky.
  drawCloudShape(ctx, ox, oy, unit, shape);
  ctx.restore();
}

// The original day.html cloud: a 130×34 pill with a big bump and a small bump.
// Each bump's centre Y (below the body top) is kept fixed; only sizes/length vary.
var BASE_BODY_W = 130;
var BASE_BODY_H = 34;
var BASE_BUMPS = [
  { d: 60, l: 20, cy: 4 },  // large bump (top -26 → centre 4)
  { d: 44, l: 70, cy: 6 },  // small bump (top -16 → centre 6)
];

/**
 * Deterministically vary the original cloud a little (in mockup px, later scaled
 * by `unit`): same flat-bottomed pill + two bumps, just a bit longer/shorter and
 * with slightly bigger/smaller bumps. Returns `minTop` (highest bump's top edge,
 * relative to the body top) so callers know the full vertical extent.
 */
export function makeCloudShape(seed) {
  var lengthF = 0.85 + seededHash01(seed, 2.3) * 0.35; // length × 0.85..1.20
  var bumpF = 0.85 + seededHash01(seed, 3.7) * 0.35;   // bumps × 0.85..1.20
  var bodyW = BASE_BODY_W * lengthF;
  var bumps = [];
  var minTop = 0;
  for (var j = 0; j < BASE_BUMPS.length; j++) {
    var base = BASE_BUMPS[j];
    // Small independent jitter so the two bumps aren't scaled in lockstep.
    var d = base.d * bumpF * (0.92 + seededHash01(seed, 11.1 + j * 5.3) * 0.16);
    var l = base.l * lengthF;
    var top = base.cy - d * 0.5; // keep the bump anchored at its centre on the body
    if (top < minTop) minTop = top;
    bumps.push({ d: d, l: l, top: top });
  }
  return { bodyW: bodyW, bodyH: BASE_BODY_H, bumps: bumps, minTop: minTop };
}

/**
 * Resolve the fill for one cloud: a solid colour (legacy blend) or a vertical
 * day/night gradient spanning the cloud's actual extent [yTop, yBot]. `boundary`
 * is the fraction from the top below which the cloud is day-white and above
 * which it is night-coloured, with a soft seam between.
 */
function cloudFill(ctx, yTop, yBot, boundary, legacyCol, alpha) {
  if (boundary == null) return rgb(legacyCol, alpha);

  var tw = 0.35; // soft seam half-width (wide → very smooth colour change)
  // Slide the seam fully off the cloud at the extremes so boundary 0 → all day
  // and boundary 1 → all night with no hard stop or solid-fill snap. The seam
  // centre travels from just above the top to just below the bottom.
  var center = -tw + boundary * (1 + 2 * tw);
  var g = ctx.createLinearGradient(0, yTop, 0, yBot);
  var steps = 6;
  for (var k = 0; k <= steps; k++) {
    var f = k / steps;
    var m = clamp01((f - (center - tw)) / (2 * tw));
    m = m * m * (3 - 2 * m); // smoothstep
    g.addColorStop(f, rgb(lerpRgb(NIGHT_CLOUD_RGB, DAY_CLOUD_RGB, m), alpha));
  }
  return g;
}

/**
 * Draw a single cloud as a UNION path (one fill, no alpha seams): a flat-bottomed
 * rounded "pill" body with round bumps on top, per the randomised `shape`.
 * @param {CanvasRenderingContext2D} ctx
 * @param {number} ox left edge of the body
 * @param {number} oy top edge of the body
 * @param {number} unit px-per-mockup-px (scaleUi × per-cloud scale)
 * @param {{ bodyW: number, bodyH: number, bumps: {d:number,l:number,top:number}[] }} shape
 */
function drawCloudShape(ctx, ox, oy, unit, shape) {
  traceCloudPath(ctx, ox, oy, unit, shape);
  ctx.fill();
}

/** Build (but don't paint) the union cloud path, so callers can fill OR clip. */
function traceCloudPath(ctx, ox, oy, unit, shape) {
  var bodyH = shape.bodyH * unit;
  var bodyW = shape.bodyW * unit;
  var r = bodyH * 0.5;
  var leftC = ox + r;
  var rightC = ox + bodyW - r;
  var cy = oy + r;

  var bumps = shape.bumps;

  ctx.beginPath();
  // Stadium body: flat top/bottom, semicircular caps.
  ctx.moveTo(leftC, oy);
  ctx.lineTo(rightC, oy);
  ctx.arc(rightC, cy, r, -Math.PI / 2, Math.PI / 2);
  ctx.lineTo(leftC, oy + bodyH);
  ctx.arc(leftC, cy, r, Math.PI / 2, Math.PI * 1.5);
  // Bumps as separate sub-paths (moveTo before each full circle).
  for (var b = 0; b < bumps.length; b++) {
    var br = bumps[b].d * 0.5 * unit;
    var bx = ox + (bumps[b].l + bumps[b].d * 0.5) * unit;
    var by = oy + (bumps[b].top + bumps[b].d * 0.5) * unit;
    ctx.moveTo(bx + br, by);
    ctx.arc(bx, by, br, 0, Math.PI * 2);
  }
}

/**
 * Fluffy texture for one cloud: clip to its silhouette, then layer soft light and
 * shadow inside it so the flat fill reads as billowing volume. No shape change —
 * just a top-lit highlight on the bumps, a cool shadow on the underside, and a
 * couple of soft inner "creases" between the lobes. Subtle, and eased down at
 * night where the cloud body is already a dark mid-tone.
 */
function applyCloudFluff(ctx, ox, oy, unit, shape, yTop, yBot, night) {
  var bodyW = shape.bodyW * unit;
  var bodyH = shape.bodyH * unit;
  var extent = yBot - yTop;
  if (bodyW <= 0 || extent <= 0) return;
  var dayAmt = 1 - 0.55 * night;

  // Each layer re-fills the EXACT cloud path (rather than clipping + a rect) so
  // the effect reaches the same anti-aliased edge as the body — including the flat
  // bottom — with no uncovered rim.
  ctx.save();

  // 1. Underside depth: a cool shadow welling up from the flat bottom so the
  //    cloud sits on its own shade instead of looking like a flat sticker.
  var shade = ctx.createLinearGradient(0, yTop, 0, yBot);
  shade.addColorStop(0, "rgba(120, 142, 175, 0)");
  shade.addColorStop(0.6, "rgba(120, 142, 175, 0)");
  shade.addColorStop(1, "rgba(96, 120, 158, " + (0.26 * dayAmt).toFixed(3) + ")");
  ctx.fillStyle = shade;
  drawCloudShape(ctx, ox, oy, unit, shape);

  // 2. Top light: a broad soft highlight pooling over the bumps, so the crowns
  //    of the cloud catch the sky light and feel rounded.
  var lobeA = 0.30 * dayAmt;
  for (var b = 0; b < shape.bumps.length; b++) {
    var bump = shape.bumps[b];
    var br = bump.d * 0.5 * unit;
    var bx = ox + (bump.l + bump.d * 0.5) * unit;
    var by = oy + (bump.top + bump.d * 0.5) * unit;
    var hx = bx - br * 0.25;
    var hy = by - br * 0.35;
    var hg = ctx.createRadialGradient(hx, hy, br * 0.1, hx, hy, br * 1.15);
    hg.addColorStop(0, "rgba(255, 255, 255, " + lobeA.toFixed(3) + ")");
    hg.addColorStop(1, "rgba(255, 255, 255, 0)");
    ctx.fillStyle = hg;
    drawCloudShape(ctx, ox, oy, unit, shape);
  }

  // 3. Inner creases: faint soft shadows low between the lobes to suggest the
  //    folds where puffs overlap.
  var creaseA = 0.16 * dayAmt;
  var creaseY = oy + bodyH * 0.62;
  var creaseR = bodyH * 1.1;
  for (var c = 0; c < 2; c++) {
    var cx = ox + bodyW * (0.32 + c * 0.4);
    var cg = ctx.createRadialGradient(cx, creaseY, 0, cx, creaseY, creaseR);
    cg.addColorStop(0, "rgba(104, 128, 165, " + creaseA.toFixed(3) + ")");
    cg.addColorStop(1, "rgba(104, 128, 165, 0)");
    ctx.fillStyle = cg;
    drawCloudShape(ctx, ox, oy, unit, shape);
  }

  ctx.restore();
}
